// src/components/CropPhotoModal/CropPhotoModal.jsx

import React, { useState, useCallback, useEffect } from "react";

import { createPortal } from "react-dom";

import Cropper from "react-easy-crop";
import { toast } from "react-toastify";

import Button from "./../../components/Button/Button";

import { FaTimes, FaSave } from "react-icons/fa";

/* ========================================================================== */
/*                                  STYLES                                    */
/* ========================================================================== */

const backdrop =
  "fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm";

/* ========================================================================== */
/*                                  HELPERS                                   */
/* ========================================================================== */

function toMasterUrlInPlace(url = "") {
  if (!url) return "";

  const [base, query = ""] = url.split("?");

  const masterBase = base.replace(/-400(\.\w+)$/, "-master$1");

  return query ? `${masterBase}?${query}` : masterBase;
}

function withCacheBust(url) {
  if (!url) return url;

  const separator = url.includes("?") ? "&" : "?";

  return `${url}${separator}cb=${Date.now()}`;
}

/* ========================================================================== */
/*                              CROP PHOTO MODAL                              */
/* ========================================================================== */

/**
 * Modale générique de recadrage 1:1.
 *
 * Props :
 *
 * - isOpen
 * - onClose
 * - entityId
 * - imageUrl
 * - cropAction
 * - onCropped
 * - format
 */
const CropPhotoModal = ({
  isOpen,
  onClose,
  entityId,
  imageUrl,
  cropAction,
  onCropped,
  format = "webp",
}) => {
  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
  });

  const [zoom, setZoom] = useState(1.2);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [saving, setSaving] = useState(false);

  const [src, setSrc] = useState("");

  const [loading, setLoading] = useState(false);

  const [loadError, setLoadError] = useState("");

  /* ======================================================================== */
  /*                           PRÉPARATION IMAGE                               */
  /* ======================================================================== */

  const prepareImage = useCallback(() => {
    if (!imageUrl) return;

    setLoading(true);
    setLoadError("");

    const master = withCacheBust(toMasterUrlInPlace(imageUrl));

    const img = new Image();

    img.crossOrigin = "anonymous";

    img.onload = () => {
      setSrc(master);
      setLoading(false);
      setLoadError("");
    };

    img.onerror = () => {
      setLoading(false);

      setLoadError("Impossible de charger la photo source (master).");
    };

    img.src = master;
  }, [imageUrl]);

  /* ======================================================================== */
  /*                              OUVERTURE                                    */
  /* ======================================================================== */

  useEffect(() => {
    if (!isOpen) return;

    prepareImage();
  }, [isOpen, prepareImage]);

  /* ======================================================================== */
  /*                       BLOQUER LE SCROLL ARRIÈRE                          */
  /* ======================================================================== */

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  /* ======================================================================== */
  /*                              CROP                                         */
  /* ======================================================================== */

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  /* ======================================================================== */
  /*                           ENREGISTREMENT                                  */
  /* ======================================================================== */

  const handleSave = async () => {
    if (!entityId || !croppedAreaPixels) {
      return;
    }

    if (loadError) {
      toast.error("L'image n'est pas chargée, recadrage impossible.");

      return;
    }

    if (typeof cropAction !== "function") {
      toast.error("Action de recadrage indisponible.");

      return;
    }

    try {
      setSaving(true);

      const response = await cropAction(entityId, {
        x: Math.round(croppedAreaPixels.x),

        y: Math.round(croppedAreaPixels.y),

        width: Math.round(croppedAreaPixels.width),

        height: Math.round(croppedAreaPixels.height),

        format,
      });

      const newUrl = response?.photo_url || response?.photoURL;

      if (!newUrl) {
        toast.warn("Recadrage effectué, mais URL non renvoyée par le serveur.");
      } else {
        toast.success("Photo recadrée.");

        onCropped?.(newUrl);
      }

      onClose?.();
    } catch (error) {
      console.error("Erreur recadrage photo :", error);

      toast.error("Échec du recadrage.");
    } finally {
      setSaving(false);
    }
  };

  /* ======================================================================== */
  /*                              RENDER                                       */
  /* ======================================================================== */

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className={backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Recadrer la photo"
    >
      <div
        className="
          relative
          w-full
          max-w-lg
          overflow-hidden
          rounded-2xl
          bg-white
          shadow-2xl
          dark:bg-slate-900
        "
        onClick={(event) => event.stopPropagation()}
      >
        {/* ================================================================ */}
        {/* HEADER                                                           */}
        {/* ================================================================ */}

        <div
          className="
            flex
            items-center
            justify-between
            border-b
            border-slate-200
            px-4
            py-3
            dark:border-slate-800
          "
        >
          <h4
            className="
              text-sm
              font-semibold
              text-slate-900
              dark:text-white
            "
          >
            Recadrer / Zoomer
          </h4>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-xl
              text-slate-500
              transition
              hover:bg-slate-100
              hover:text-slate-900
              disabled:opacity-50
              dark:text-slate-400
              dark:hover:bg-slate-800
              dark:hover:text-white
            "
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* ================================================================ */}
        {/* CROPPER                                                          */}
        {/* ================================================================ */}

        <div className="relative h-[55vh] bg-black">
          {loading && (
            <div
              className="
                absolute
                inset-0
                flex
                items-center
                justify-center
                text-sm
                text-white
              "
            >
              Chargement de l’image…
            </div>
          )}

          {loadError && (
            <div
              className="
                absolute
                inset-0
                flex
                flex-col
                items-center
                justify-center
                gap-3
                p-4
                text-center
                text-white
              "
            >
              <div className="text-sm">{loadError}</div>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={(event) => {
                  event.stopPropagation();
                  prepareImage();
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          {!loading && !loadError && src && (
            <Cropper
              key={src}
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              restrictPosition
              mediaProps={{
                crossOrigin: "anonymous",

                draggable: false,
              }}
              objectFit="contain"
            />
          )}
        </div>

        {/* ================================================================ */}
        {/* FOOTER                                                           */}
        {/* ================================================================ */}

        <div className="space-y-4 p-4">
          <div className="flex items-center gap-3">
            <span
              className="
                w-28
                text-sm
                font-medium
                text-slate-700
                dark:text-slate-300
              "
            >
              Zoom
            </span>

            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(parseFloat(event.target.value))}
              className="range range-sm flex-1"
              disabled={Boolean(loadError) || loading}
            />
          </div>

          <div
            className="
              flex
              justify-end
              gap-2
              border-t
              border-slate-200
              pt-4
              dark:border-slate-800
            "
          >
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={(event) => {
                event.stopPropagation();

                onClose?.();
              }}
              className="gap-2"
              disabled={saving}
            />

            <Button
              text={saving ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              onClick={handleSave}
              disabled={saving || Boolean(loadError) || loading}
              className="gap-2"
            />
          </div>
        </div>
      </div>
    </div>
  );

  /* ======================================================================== */
  /*                                PORTAL                                    */
  /* ======================================================================== */

  return createPortal(modalContent, document.body);
};

export default CropPhotoModal;

/* ========================================================================== */
/*                              EXPORT HELPER                                 */
/* ========================================================================== */

export function toMasterUrl(thumbUrl = "") {
  if (!thumbUrl) return "";

  return thumbUrl.replace(/-400(\.\w+)$/, "-master$1");
}
